import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import time from 'utils/time';
import { store } from 'components/Store';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import ModalComponent from 'components/Common/Modal';
import LogInteractionModal from 'components/Dashboard/Influence/CrmInteractions/LogInteractionModal/LogInteractionModal';
import useCrmInteractionsQuery from 'components/Dashboard/Influence/CrmInteractions/useCrmInteractionsQuery';
import getInteractionDisplayType from 'components/Dashboard/Influence/CrmInteractions/getInteractionDisplayType';
import FiltersPopup from 'components/Dashboard/Filters/ui/FiltersPopUp';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import useCompileFiltersFunction from 'components/Dashboard/Filters/useCompileFilters';
import useOnScreen from 'components/Dashboard/hooks/useOnScreen';
import useSendEmailToContacts from 'components/Dashboard/Influence/CrmContacts/CrmContactsTable/CrmContactsListBar/sendEmailToContacts';
import { useNavigate } from 'react-router-dom';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useEmailRecipientInfo from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/EmailRecipients/useEmailRecipientInfo';
import AdditionalRecipientInfo from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/EmailRecipients/AdditionalRecipientInfo';

const Interactions = (props) => {
  const { StakeholderInformation, onInteractionsChanged } = props;
  const { id, contactInformation, firstName, lastName, imageId, imageUrl, email } = StakeholderInformation;
  // Interactions are linked by CrmContact.id, not the generic stakeholder id — for a
  // stakeholder profile page (not a CRM contact edit page) that's contactInformation[0].id.
  // On a CRM contact edit page StakeholderInformation already *is* the CrmContact record.
  const crmContact =
    contactInformation?.[0] ?? (firstName || lastName ? { id, firstName, lastName, imageId, imageUrl, email } : null);
  const crmContactId = crmContact?.id ?? id;
  const preselectedStakeholders = crmContact
    ? [
        {
          value: crmContact.id,
          id: crmContact.id,
          label: `${crmContact.firstName ?? ''} ${crmContact.lastName ?? ''}`.trim(),
          imageId: crmContact.imageId,
          imageUrl: crmContact.imageUrl,
          email: crmContact.email,
        },
      ]
    : [];

  const [showLogModal, setShowLogModal] = useState(false);
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);
  const [editingInteractionId, setEditingInteractionId] = useState(null);
  const [deletingInteraction, setDeletingInteraction] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingSendEmail, setIsLoadingSendEmail] = useState(false);

  const isMobile = useIsMobile(1280);

  const { state } = useContext(store);
  const { listDomainsVerified } = state;
  const { generalApiCall } = useGeneralApiCall();
  const { sendEmailToContacts } = useSendEmailToContacts();
  const navigate = useNavigate();

  const handleAddToEmailCampaign = () => {
    const unverifiedEmail =
      !listDomainsVerified.verifiedDomains.length && !listDomainsVerified.verifiedEmailAddresses.length;
    if (unverifiedEmail) {
      navigate('/people/emails', { state: { setUpDomain: true }, replace: true });
      return;
    }
    sendEmailToContacts({
      contactsToSendIds: [crmContactId],
      query: new URLSearchParams(),
      requestSource: new AbortController(),
      filters: [],
      setIsLoading: setIsLoadingSendEmail,
      returnUrl: `${window.location.pathname}${window.location.search}`,
    });
  };

  const { getDefaultSectionFilters, clearFilters } = useFilters();
  const { compileFiltersFunction } = useCompileFiltersFunction('CrmInteractions');

  const sectionFilters = getDefaultSectionFilters(null, 'CrmInteractions');
  const { filters: compiledFilters, hash } = compileFiltersFunction(sectionFilters);

  const activeFilterCount = sectionFilters.reduce((count, filter) => {
    if (filter.type === 'search') return filter.value ? count + 1 : count;
    return filter.options?.some((o) => o.selected) ? count + 1 : count;
  }, 0);

  const filters = [
    { field: 'crmContactIds', operator: 'str_eq', value: String(crmContactId) },
    ...(compiledFilters ?? []),
  ];

  const {
    interactions,
    isLoading,
    isLoadingScroll,
    thereIsMoreContent,
    handleScrollBottom,
    addInteraction,
    updateInteractionLocal,
    removeInteraction,
  } = useCrmInteractionsQuery({
    filters,
    sort: { field: 'createdAtUtc', order: 'DESC' },
    dependencyKey: `${crmContactId}-${hash}`,
    enabled: !!crmContactId,
  });

  const [heightContainer, containerRef] = useHeightContainer();

  const sentinelRef = useRef(null);
  const isSentinelVisible = useOnScreen(sentinelRef, interactions?.length, '200px 0px 200px 0px');

  useEffect(() => {
    if (isSentinelVisible && thereIsMoreContent && !isLoading && !isLoadingScroll) {
      handleScrollBottom();
    }
  }, [isSentinelVisible, thereIsMoreContent, isLoading, isLoadingScroll]);

  const belongsToStakeholder = (interaction) =>
    (interaction.crmContactIds ?? []).some((id) => Number(id) === Number(crmContactId));

  const handleDeleteInteraction = async () => {
    setIsDeleting(true);
    // The delete endpoint responds 200 with an empty body, so `result` (response.data) would be
    // an empty, falsy string — request the full response and check the status instead.
    const response = await generalApiCall({
      method: 'post',
      pathname: '/api/crm-interaction/delete',
      requestProperties: [deletingInteraction.id],
      needsAuthentication: true,
      returnCompleteRequest: true,
    });
    setIsDeleting(false);
    if (response?.status === 200) {
      removeInteraction(deletingInteraction.id);
      onInteractionsChanged?.();
      setDeletingInteraction(null);
      createNotification('success', 'Interaction deleted successfully');
    }
  };

  return (
    <div className='border-bottom'>
      <div
        className='px-4 specific-bottom-padding-31 specific-top-padding-7 d-flex align-items-center stakeholder-interaction-actions'
        style={{ minHeight: `75.6px` }}
      >
        <div
          className='d-flex align-items-center justify-content-between stakeholder-interaction-actions-scroll stakeholder-interaction-actions-content'
          style={{ flex: '1 1 auto', minWidth: 0, overflowX: 'auto' }}
        >
          <button
            className='text-left action-state-hover general-button specific-y-padding-6 pr-3 specific-left-padding-15 text-light-blue border-general border-light-blue rounded-pill d-flex align-items-center nowrap-item flex-shrink-0'
            onClick={() => setShowLogModal(true)}
          >
            <span className='icon-chat mr-2 title-h4' style={{ transform: 'translateY(1px)' }} />
            <span className='mb-0'>{isMobile ? 'Interaction' : 'Log interaction'}</span>
          </button>

          {activeFilterCount === 0 && (
            <button
              className='text-left action-state-hover general-button specific-y-padding-6 pr-3 specific-left-padding-15 text-light-blue border-general border-light-blue rounded-pill d-flex align-items-center nowrap-item flex-shrink-0'
              onClick={handleAddToEmailCampaign}
              disabled={isLoadingSendEmail}
            >
              {isLoadingSendEmail ? (
                <i className='fas fa-spinner fa-spin mr-2 ml-1' />
              ) : (
                <span className='icon-email mr-2 title-h5' />
              )}
              <span className='mb-0'>{isMobile ? 'Email' : 'Add to email campaign'}</span>
            </button>
          )}
          <div className='d-flex flex-shrink-0'>
            {activeFilterCount > 0 && (
              <button className='general-button action-delete-text flex-shrink-0 mr-2' onClick={() => clearFilters()}>
                Clear filters
              </button>
            )}
            <button
              className={`sort-button more-filters-button flex-shrink-0 ${activeFilterCount > 0 ? 'more-filters-button-active' : ''}`}
              style={{ height: '38px' }}
              onClick={() => setShowFiltersPopup(true)}
            >
              <span className='icon-filters' />
              <span className='ml-1 paragraph-p1'>Filters</span>
              {activeFilterCount > 0 && <span className='ml-1'>({activeFilterCount})</span>}
            </button>
          </div>
        </div>
      </div>

      <div className='border-top pt-2 pb-4' ref={containerRef} style={{ minHeight: `${heightContainer - 1}px` }}>
        {isLoading ? (
          <LoadingStateInteractions />
        ) : interactions?.length > 0 ? (
          <>
            {interactions.map((item) => (
              <InteractionItem
                key={`interaction-${item.id}`}
                item={item}
                crmContactId={crmContactId}
                isOwner={item.addedByUserEmailAddress === state.team?.myself?.emailAddress}
                onEdit={() => setEditingInteractionId(item.id)}
                onDelete={() => setDeletingInteraction({ id: item.id, title: item.title })}
              />
            ))}
            <div ref={sentinelRef} />
            {isLoadingScroll && <LoadingStateInteractions count={1} />}
          </>
        ) : (
          <div
            className='flex-centered justify-content-center text-center'
            style={{ height: `${heightContainer - 36}px` }}
          >
            <p className='main-light-text title-h5-m' style={{ maxWidth: '420px' }}>
              {activeFilterCount > 0
                ? 'No interactions match the current filters'
                : 'No interactions logged with this stakeholder yet'}
            </p>
          </div>
        )}
      </div>

      {showLogModal && (
        <LogInteractionModal
          onClose={() => setShowLogModal(false)}
          preselectedStakeholders={preselectedStakeholders}
          onSuccess={(newInteraction) => {
            if (belongsToStakeholder(newInteraction)) {
              addInteraction(newInteraction);
              onInteractionsChanged?.();
            }
            setShowLogModal(false);
          }}
        />
      )}

      <FiltersPopup
        section='CrmInteractions'
        openState={{ isOpen: showFiltersPopup, setIsOpen: setShowFiltersPopup }}
      />

      {editingInteractionId && (
        <LogInteractionModal
          interactionId={editingInteractionId}
          onClose={() => setEditingInteractionId(null)}
          onSuccess={(updatedInteraction) => {
            if (belongsToStakeholder(updatedInteraction)) updateInteractionLocal(updatedInteraction);
            else removeInteraction(updatedInteraction.id);
            setEditingInteractionId(null);
          }}
        />
      )}

      <ModalComponent maxWidth={480} isOpen={!!deletingInteraction} setIsOpen={() => setDeletingInteraction(null)}>
        <div className='p-4 px-lg-5 pt-5 pb-lg-2'>
          <div className='text-center'>
            <p>Are you sure you want to delete this interaction?</p>
            <div className='my-4'>
              <button
                className='general-button outline-button general-button-spacing mr-2 mr-lg-3 rounded-pill'
                onClick={() => setDeletingInteraction(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className={`general-button general-button-spacing delete-note-button ${isDeleting ? '' : 'text-white'} rounded-pill`}
                onClick={handleDeleteInteraction}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete interaction'}
              </button>
            </div>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

const InteractionItem = ({ item, crmContactId, isOwner, onEdit, onDelete }) => {
  const { dateTime, addedByUserFullName, description, sourceCrmEmailId } = item;
  const isEmailCampaign = !!sourceCrmEmailId;
  const displayType = getInteractionDisplayType(item);
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const [isExpandable, setIsExpandable] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const { generalApiCall } = useGeneralApiCall();
  const [subject, setSubject] = useState(null);
  const { getRecipientInfo, isLoading: isLoadingRecipientInfo, recipientInfo } = useEmailRecipientInfo();
  const hasLoadedRecipientInfoRef = useRef(false);

  useLayoutEffect(() => {
    // Email campaigns don't show the body here at all (see subject fetch below) - there's no text
    // to overflow-measure, and they're always expandable (to reveal the send/open/click timeline).
    if (isEmailCampaign) return;
    if (descriptionRef.current) {
      // clientHeight is capped by the collapsed max-height (~1 line); scrollHeight is the
      // paragraph's real, unclipped height. If the real content needs more room, it's expandable.
      // A huge placeholder max-height (e.g. 1000px) would make the transition interpolate over
      // a range far bigger than the real content, so the visible growth finishes almost
      // instantly — animate to the actual measured height instead.
      setFullHeight(descriptionRef.current.scrollHeight + 2);
      setIsExpandable(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight + 1);
    }
  }, [description, isEmailCampaign]);

  // The interaction list/search endpoint has no title field at all (only the raw description) -
  // fetch it from the interaction itself, not the email: CreateInteractionFromEmailAsync already
  // sets an email-campaign interaction's own Title to the email's subject at creation time, so
  // there's no need to pull the full email (body, recipients, etc.) just for this one string.
  useEffect(() => {
    if (!isEmailCampaign) return;
    const source = new AbortController();
    (async () => {
      const result = await generalApiCall({
        method: 'get',
        pathname: `/api/crm-interaction/${item.id}`,
        needsAuthentication: true,
        requestSource: source,
        notShowErrorMessage: true,
      });
      if (result) setSubject(result.title ?? null);
    })();
    return () => source.abort('Interaction item title fetch cancelled');
  }, [isEmailCampaign, item.id]);

  // Same per-recipient send/open/click history already shown on the email performance page -
  // loaded lazily on first expand rather than for every collapsed row in the list.
  useEffect(() => {
    if (!isEmailCampaign || !isExpanded || hasLoadedRecipientInfoRef.current) return;
    hasLoadedRecipientInfoRef.current = true;
    getRecipientInfo({ emailId: sourceCrmEmailId, contactId: crmContactId });
  }, [isEmailCampaign, isExpanded]);

  const expandable = isEmailCampaign || isExpandable;

  return (
    <div
      className={`position-relative stakeholder-interaction-item border-bottom ${isExpanded ? '' : 'pb-3'} ${expandable ? 'pointer' : ''}`}
      onClick={expandable ? () => setIsExpanded((prev) => !prev) : undefined}
    >
      <div className='px-4 pt-3'>
        <div className='d-flex justify-content-between align-items-start'>
          <p className='mb-1 paragraph-p2 main-light-text'>
            <span className='text-main-blue font-weight-bold'>{displayType}</span>
            <span className='dot d-inline-block-centered'></span>
            {addedByUserFullName}
            <span className='dot d-inline-block-centered'></span>
            {time(dateTime).format('DD MMM[,] YYYY')}
          </p>
          {isOwner && (
            <div className='d-flex flex-shrink-0 ml-2 specific-right-margin-20' style={{ gap: 4 }}>
              <button
                data-tooltip-content='Edit interaction'
                data-tooltip-id='general-tooltip'
                className='general-button circle-button hover-background-gray-05 p-1'
                onClick={(e) => {
                  e.stopPropagation();
                  // Without this, the tooltip can get stuck showing after the modal (which covers
                  // the button while open) closes, since no real mouseleave ever reaches it.
                  e.currentTarget.blur();
                  onEdit();
                }}
              >
                <span className='icon-edit-bold main-subtle-text paragraph-p2' />
              </button>
              <button
                data-tooltip-content='Delete interaction'
                data-tooltip-id='general-tooltip'
                className='general-button circle-button hover-background-gray-05 p-1'
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.blur();
                  onDelete();
                }}
              >
                <span className='icon-delete main-subtle-text paragraph-p3' />
              </button>
            </div>
          )}
        </div>
        {isEmailCampaign ? (
          <p
            className='mb-0 paragraph-p2 main-light-text pre-wrap interaction-description'
            style={{ lineHeight: 1.35 }}
          >
            {subject ?? '…'}
          </p>
        ) : description ? (
          <p
            ref={descriptionRef}
            className='mb-0 paragraph-p2 main-light-text pre-wrap interaction-description'
            style={{ lineHeight: 1.35, maxHeight: isExpanded ? fullHeight : undefined }}
          >
            {description}
          </p>
        ) : (
          <p className='mb-0 paragraph-p2 main-light-text'>No description added</p>
        )}
        {expandable && <span className={`dropdown-item-element ${isExpanded ? 'open' : ''}`} />}
      </div>
      {isEmailCampaign && isExpanded && (
        <div className='email-recipients-body pt-0'>
          <AdditionalRecipientInfo isLoading={isLoadingRecipientInfo} info={recipientInfo} />
        </div>
      )}
    </div>
  );
};

const LoadingStateInteractions = ({ count = 2 }) => (
  <>
    {Array(count)
      .fill()
      .map((_, index) => (
        <SkeletonTheme baseColor='#fff' key={index}>
          <div className='px-4 pt-3 pb-2'>
            <div className='d-flex align-items-start mb-2'>
              <Skeleton height={22} width={80} className='rounded-pill mr-2' />
              <div className='pl-1'>
                <Skeleton height={16} width={280} />
                <div className='mt-1'>
                  <Skeleton height={14} width={160} />
                </div>
              </div>
            </div>
          </div>
        </SkeletonTheme>
      ))}
  </>
);

export default Interactions;
